<app-website-header></app-website-header>

<!-- Loading State -->
<div class="text-center py-5" *ngIf="isLoading"
    style="min-height: 50vh; display: flex; flex-direction: column; justify-content: center;">
    <i class="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
    <p class="text-muted h5">Loading product details...</p>
</div>

<!-- Error/Not Found State -->
<div class="container py-5" *ngIf="!isLoading && !product">
    <div class="alert alert-warning text-center">
        <h4>Product Not Found</h4>
        <p>The product you are looking for does not exist or is no longer available.</p>
        <a routerLink="/products" class="btn btn-primary mt-3"><i class="fas fa-arrow-left"></i> Back to Products</a>
    </div>
</div>

<!-- Product Details Section -->
<section class="product-detail-section" *ngIf="!isLoading && product">
    <div class="container">
        <!-- Breadcrumb -->
        <div class="breadcrumb-container">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item"><a routerLink="/home"><i class="fas fa-home mr-1"></i> Home</a></li>
                    <li class="breadcrumb-item"><a routerLink="/products">Products</a></li>
                    <li class="breadcrumb-item active" aria-current="page">{{ product.productName }}</li>
                </ol>
            </nav>
        </div>

        <div class="row gx-5">
            <!-- Left Column: Images -->
            <div class="col-lg-5 mb-5 mb-lg-0">
                <div class="product-gallery">
                    <!-- Main Image -->
                    <div class="main-image-card">
                        <img [src]="selectedImage" [alt]="product.productName">
                        <span class="status-badge" *ngIf="product.availability === 'IN_STOCK'">
                            In Stock
                        </span>
                    </div>

                    <!-- Thumbnails -->
                    <div class="product-thumbnails mt-4"
                        *ngIf="product.imageDetails && product.imageDetails.length > 1">
                        <div class="thumbnail" *ngFor="let img of product.imageDetails"
                            [class.active]="selectedImage === getThumbnailUrl(img)" (click)="selectImage(img)">
                            <img [src]="getThumbnailUrl(img)" [alt]="product.productName">
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Column: Product Info -->
            <div class="col-lg-7">
                <div class="product-info-container">
                    <!-- Title & Basic Info -->
                    <h1 class="product-title">{{ product.productName }}</h1>
                    <div class="product-ref"><i class="fas fa-barcode"></i> Ref: {{ product.productCode }}</div>

                    <p class="product-description" *ngIf="product.description">
                        {{ product.description }}
                    </p>

                    <!-- Contact Action -->
                    <div class="premium-action-card">
                        <div class="action-header">
                            <h4>Interested in this machine?</h4>
                            <p>Get in touch with our team for pricing, specifications tailored to your needs, and
                                availability.</p>
                        </div>
                        <div class="action-buttons">
                            <button class="btn-quote" (click)="openEnquiryModal()">
                                <i class="fas fa-paper-plane"></i> Request Quote
                            </button>
                            <a href="tel:+919513266444" class="btn-call">
                                <i class="fas fa-phone-alt"></i> Call Us
                            </a>
                        </div>
                    </div>

                    <!-- Models & Specifications -->
                    <div class="models-section" *ngIf="product.models && product.models.length > 0">
                        <h3 class="section-title"><i class="fas fa-cogs"></i> Technical Specifications</h3>

                        <div class="premium-accordion" id="modelsAccordion">
                            <div class="spec-card" *ngFor="let model of product.models; let i = index">
                                <div class="card-header" [id]="'heading' + i">
                                    <button class="btn-link" type="button" data-toggle="collapse"
                                        [attr.data-target]="'#collapse' + i"
                                        [attr.aria-expanded]="i === 0 ? 'true' : 'false'"
                                        [attr.aria-controls]="'collapse' + i">
                                        <span class="model-name"><i class="fas fa-cube"></i> {{ model.modelName
                                            }}</span>
                                        <i class="fas fa-chevron-down toggle-icon"></i>
                                    </button>
                                </div>

                                <div [id]="'collapse' + i" class="collapse" [class.show]="i === 0"
                                    [attr.aria-labelledby]="'heading' + i" data-parent="#modelsAccordion">
                                    <div class="card-body">
                                        <table class="table"
                                            *ngIf="model.specificationValues && model.specificationValues.length > 0">
                                            <tbody>
                                                <tr *ngFor="let specValue of model.specificationValues">
                                                    <th>{{ specValue.specification?.specName }}</th>
                                                    <td>
                                                        {{ specValue.value }}
                                                        {{ specValue.specification?.unit === 'N/A' ? '' :
                                                        (specValue.specification?.unit || '') }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <div *ngIf="!model.specificationValues || model.specificationValues.length === 0"
                                            class="empty-specs">
                                            No specifications available for this model.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</section>

<!-- Additional Information Tabs (Optional/Future expansion) -->
<section class="additional-info-section" *ngIf="!isLoading && product">
    <div class="container">
        <div class="row justify-content-center text-center">
            <div class="col-md-8">
                <h2 class="section-title">Why Choose AMT Machineries?</h2>
                <div class="title-divider"></div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-4 mb-4 mb-md-0">
                <div class="feature-item">
                    <div class="icon-circle">
                        <i class="fas fa-check-circle"></i>
                    </div>
                    <h4>Quality Assured</h4>
                    <p>Every machine undergoes rigorous testing to meet strict performance and durability standards.</p>
                </div>
            </div>
            <div class="col-md-4 mb-4 mb-md-0">
                <div class="feature-item">
                    <div class="icon-circle">
                        <i class="fas fa-tools"></i>
                    </div>
                    <h4>Expert Support</h4>
                    <p>Installation, commissioning, and reliable after-sales service from our skilled technical team.
                    </p>
                </div>
            </div>
            <div class="col-md-4">
                <div class="feature-item">
                    <div class="icon-circle">
                        <i class="fas fa-shield-alt"></i>
                    </div>
                    <h4>Genuine Parts</h4>
                    <p>We supply authentic replacement parts to guarantee the longevity of your investment.</p>
                </div>
            </div>
        </div>
    </div>
</section>

<!-- Enquiry Modal -->
<div class="enquiry-modal-backdrop" *ngIf="isEnquiryModalOpen">
    <div class="enquiry-modal">
        <div class="enquiry-modal-header border-bottom-0 pb-0">
            <div>
                <h5 class="m-0 "><i class="fas fa-paper-plane mr-2 text-primary"></i> Request
                    a Quote</h5>
                <small class="text-light mt-1 d-block">For: {{ product?.productName }}</small>
            </div>
            <button class="close-btn text-muted" (click)="closeEnquiryModal()">&times;</button>
        </div>
        <div class="enquiry-modal-body pt-4">
            <div class="row">
                <div class="col-md-6 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">Customer Name <span
                            class="text-danger">*</span></label>
                    <input type="text" class="form-control form-control-lg bg-light border-0"
                        [(ngModel)]="enquiryForm.customerName" placeholder="Your full name">
                </div>
                <div class="col-md-6 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">Phone Number <span
                            class="text-danger">*</span></label>
                    <input type="text" class="form-control form-control-lg bg-light border-0"
                        [(ngModel)]="enquiryForm.phoneNumber" placeholder="Your phone number">
                </div>
            </div>
            <div class="row">
                <div class="col-md-4 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">Door No</label>
                    <input type="text" class="form-control bg-light border-0" [(ngModel)]="enquiryForm.doorNo"
                        placeholder="Door / Plot number">
                </div>
                <div class="col-md-4 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">Street</label>
                    <input type="text" class="form-control bg-light border-0" [(ngModel)]="enquiryForm.street"
                        placeholder="Street name">
                </div>
                <div class="col-md-4 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">Village</label>
                    <input type="text" class="form-control bg-light border-0" [(ngModel)]="enquiryForm.village"
                        placeholder="Village / Area">
                </div>
            </div>
            <div class="row">
                <div class="col-md-4 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">City</label>
                    <input type="text" class="form-control bg-light border-0" [(ngModel)]="enquiryForm.city"
                        placeholder="City">
                </div>
                <div class="col-md-4 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">State</label>
                    <input type="text" class="form-control bg-light border-0" [(ngModel)]="enquiryForm.state"
                        placeholder="State">
                </div>
                <div class="col-md-4 form-group">
                    <label class="font-weight-bold text-dark small text-uppercase">Pincode</label>
                    <input type="text" class="form-control bg-light border-0" [(ngModel)]="enquiryForm.pincode"
                        placeholder="Pincode">
                </div>
            </div>
            <div class="form-group">
                <label class="font-weight-bold text-dark small text-uppercase">Message / Custom Requirements</label>
                <textarea class="form-control bg-light border-0" [(ngModel)]="enquiryForm.message" rows="4"
                    placeholder="Tell us about your specific production needs..."></textarea>
            </div>
            <div class="text-right mt-4 pt-3 border-top">
                <button type="button" class="btn btn-light px-4 mr-2" (click)="closeEnquiryModal()">Cancel</button>
                <button type="button" class="btn btn-primary px-4" (click)="submitEnquiry()" [disabled]="isSubmitting">
                    <i class="fas fa-paper-plane mr-1" *ngIf="!isSubmitting"></i>
                    <i class="fas fa-spinner fa-spin mr-1" *ngIf="isSubmitting"></i>
                    {{ isSubmitting ? 'Sending Enquiry...' : 'Send Enquiry' }}
                </button>
            </div>
        </div>
    </div>
</div>

<app-website-footer></app-website-footer>